/* ==========================================================================
   Sultana — motion
   Scroll reveals, split headings, header behaviour, magnetic buttons,
   parallax, stacking cards, the reviews rail,
   the variable-weight title and the circular theme switch. Everything re-initialises when a section is
   reloaded in the theme editor.
   ========================================================================== */
(function () {
  var html = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var isRTL = html.getAttribute('dir') === 'rtl';
  html.classList.add('s-motion-ready');

  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  function lerp(a, b, t) { return a + (b - a) * t; }

  /* One rAF-throttled scroll/resize loop shared by every effect */
  var scrollFns = [];
  var ticking = false;
  function onScroll(fn) { scrollFns.push(fn); fn(); }
  function runScroll() {
    ticking = false;
    for (var i = 0; i < scrollFns.length; i++) scrollFns[i]();
  }
  function requestScroll() { if (!ticking) { ticking = true; requestAnimationFrame(runScroll); } }
  window.addEventListener('scroll', requestScroll, { passive: true });
  window.addEventListener('resize', requestScroll);

  /* ---------- Split headings into words ---------- */
  function gluePunctuation(el) {
    Array.prototype.slice.call(el.querySelectorAll('em, strong, span')).forEach(function (inline) {
      var next = inline.nextSibling;
      if (!next || next.nodeType !== 3) return;
      var m = next.textContent.match(/^[^\s]+/);
      if (!m) return;
      var last = inline.lastChild;
      if (last && last.nodeType === 3) last.textContent += m[0];
      else inline.appendChild(document.createTextNode(m[0]));
      next.textContent = next.textContent.slice(m[0].length);
    });
  }

  function splitWords(el) {
    if (el.dataset.sSplitDone) return;
    el.dataset.sSplitDone = '1';
    gluePunctuation(el);
    var i = 0;
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var parts = child.textContent.split(/(\s+)/);
          var frag = document.createDocumentFragment();
          parts.forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var w = document.createElement('span');
            w.className = 's-w';
            var inner = document.createElement('span');
            inner.className = 's-w__i';
            inner.style.setProperty('--i', i++);
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          });
          child.parentNode.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    })(el);
  }

  /* ---------- Reveal on scroll ---------- */
  /* An element fully hidden by clip-path never reports as intersecting, so arch
     reveals are observed through their parent and mapped back here. */
  var proxied = new Map();
  var revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var targets = proxied.get(entry.target) || [entry.target];
      targets.forEach(function (t) { t.classList.add('is-in'); });
      proxied.delete(entry.target);
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }) : null;
  function observeReveal(el) {
    if (el.getAttribute('data-s-reveal') !== 'arch' || !el.parentElement) { revealObserver.observe(el); return; }
    var parent = el.parentElement;
    if (!proxied.has(parent)) proxied.set(parent, []);
    proxied.get(parent).push(el);
    revealObserver.observe(parent);
  }

  function initReveals(root) {
    $$('[data-s-split]', root).forEach(splitWords);
    $$('[data-s-stagger]', root).forEach(function (group) {
      Array.prototype.forEach.call(group.children, function (child, idx) {
        child.style.setProperty('--i', idx);
      });
    });
    /* No layout reads here: the hero plays its entrance straight away and
       everything else is handed to the IntersectionObserver (asynchronous). */
    $$('[data-s-reveal], [data-s-split]', root).forEach(function (el) {
      if (el.classList.contains('is-in')) return;
      if (!revealObserver || reduce) { el.classList.add('is-in'); return; }
      if (el.closest('.s-hero')) {
        requestAnimationFrame(function () { requestAnimationFrame(function () { el.classList.add('is-in'); }); });
      } else {
        observeReveal(el);
      }
    });
  }

  /* ---------- Header: glass after scrolling, hides going down ---------- */
  function initHeader() {
    var header = document.querySelector('.s-header');
    if (!header) return;
    var lastY = window.scrollY;
    /* Header height without a synchronous layout read at start-up */
    if ('ResizeObserver' in window) {
      new ResizeObserver(function (entries) {
        var box = entries[0].borderBoxSize && entries[0].borderBoxSize[0];
        var h = box ? box.blockSize : entries[0].target.offsetHeight;
        html.style.setProperty('--s-header-h', Math.round(h) + 'px');
      }).observe(header);
    }
    onScroll(function () {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      var drawerOpen = document.querySelector('.s-drawer[open]');
      if (!drawerOpen && header.classList.contains('is-sticky')) {
        if (y > lastY + 6 && y > 240) header.classList.add('is-hidden');
        else if (y < lastY - 6 || y < 240) header.classList.remove('is-hidden');
      }
      lastY = y;
    });
    $$('.s-drawer .s-nav li', header).forEach(function (li, i) {
      var a = li.querySelector('.s-nav__link');
      if (a) a.style.setProperty('--i', i);
    });
  }

  /* ---------- Magnetic buttons ---------- */
  function initMagnetic(root) {
    if (!finePointer || reduce) return;
    $$('[data-s-magnetic]', root).forEach(function (el) {
      if (el.dataset.sMagDone) return;
      el.dataset.sMagDone = '1';
      var strength = parseFloat(el.getAttribute('data-s-magnetic')) || 0.35;
      el.style.transition = 'transform .6s cubic-bezier(.16,1,.3,1)';
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left - r.width / 2) * strength;
        var y = (e.clientY - r.top - r.height / 2) * strength;
        el.style.transform = 'translate(' + x + 'px,' + y + 'px)';
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* ---------- Spotlight that follows the pointer ---------- */
  function initGlow(root) {
    if (!finePointer) return;
    $$('[data-s-glow]', root).forEach(function (el) {
      if (el.dataset.sGlowDone) return;
      el.dataset.sGlowDone = '1';
      var target = el.querySelector('.s-hero__glow') || el;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        target.style.setProperty('--mx', ((e.clientX - r.left) / r.width) * 100 + '%');
        target.style.setProperty('--my', ((e.clientY - r.top) / r.height) * 100 + '%');
      });
    });
  }

  /* ---------- Parallax (uses the `translate` property, leaves `transform` to reveals) ---------- */
  var parallaxEls = [];
  function initParallax(root) {
    if (reduce) return;
    $$('[data-s-parallax]', root).forEach(function (el) {
      if (parallaxEls.indexOf(el) === -1) parallaxEls.push(el);
    });
  }
  onScroll(function () {
    var vh = window.innerHeight;
    parallaxEls.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      var speed = parseFloat(el.getAttribute('data-s-parallax')) || 0.1;
      var offset = (r.top + r.height / 2 - vh / 2) * speed;
      el.style.translate = '0 ' + offset.toFixed(1) + 'px';
    });
  });

  /* ---------- Stacking cards: the covered card sinks back ---------- */
  var stacks = [];
  function initStacks(root) {
    if (reduce) return;
    $$('[data-s-stack]', root).forEach(function (s) {
      if (stacks.indexOf(s) === -1) stacks.push(s);
    });
  }
  onScroll(function () {
    stacks.forEach(function (stack) {
      var cards = $$('.s-stack__card', stack);
      cards.forEach(function (card, i) {
        var next = cards[i + 1];
        if (!next) { card.style.transform = ''; card.style.filter = ''; return; }
        var a = card.getBoundingClientRect();
        var b = next.getBoundingClientRect();
        var covered = clamp(1 - (b.top - a.top) / a.height, 0, 1);
        card.style.transform = 'scale(' + (1 - covered * 0.06).toFixed(3) + ')';
        card.style.filter = 'brightness(' + (1 - covered * 0.35).toFixed(3) + ')';
      });
    });
  });

  /* ---------- Rails: drag to scroll + arrows ---------- */
  function initRails(root) {
    $$('[data-s-rail]', root).forEach(function (rail) {
      if (rail.dataset.sRailDone) return;
      rail.dataset.sRailDone = '1';
      var wrap = rail.closest('[data-s-rail-wrap]') || rail.parentNode;
      var prev = wrap.querySelector('[data-dir="-1"]');
      var next = wrap.querySelector('[data-dir="1"]');
      function step() {
        var first = rail.firstElementChild;
        return first ? first.getBoundingClientRect().width + 24 : rail.clientWidth * 0.8;
      }
      function update() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        var pos = Math.abs(rail.scrollLeft);
        if (prev) prev.disabled = pos <= 2;
        if (next) next.disabled = pos >= max;
      }
      [prev, next].forEach(function (btn) {
        if (!btn) return;
        btn.addEventListener('click', function () {
          var dir = parseInt(btn.getAttribute('data-dir'), 10) * (isRTL ? -1 : 1);
          rail.scrollBy({ left: dir * step(), behavior: reduce ? 'auto' : 'smooth' });
        });
      });
      rail.addEventListener('scroll', update, { passive: true });
      update();
      if (!finePointer) return;
      var down = false, startX = 0, startLeft = 0, moved = false;
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false; startX = e.clientX; startLeft = rail.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) { moved = true; rail.classList.add('is-dragging'); }
        rail.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        rail.classList.remove('is-dragging');
      });
      rail.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    });
  }

  /* ---------- Variable-weight title: letters swell near the cursor ---------- */
  function initWeight(root) {
    $$('[data-s-weight]', root).forEach(function (title) {
      if (title.dataset.sWeightDone) return;
      title.dataset.sWeightDone = '1';
      var byWord = isRTL; /* Arabic letters must stay joined */
      (function walk(node) {
        Array.prototype.slice.call(node.childNodes).forEach(function (child) {
          if (child.nodeType === 3) {
            var frag = document.createDocumentFragment();
            var units = byWord ? child.textContent.split(/(\s+)/) : Array.from(child.textContent);
            units.forEach(function (u) {
              if (!u) return;
              if (/^\s+$/.test(u)) { frag.appendChild(document.createTextNode(u)); return; }
              var s = document.createElement('span');
              s.className = 's-ch';
              s.textContent = u;
              frag.appendChild(s);
            });
            child.parentNode.replaceChild(frag, child);
          } else if (child.nodeType === 1 && child.tagName !== 'BR') {
            walk(child);
          }
        });
      })(title);
      if (!finePointer || reduce) return;
      var chars = $$('.s-ch', title);
      var zone = title.closest('section') || title;
      zone.addEventListener('pointermove', function (e) {
        chars.forEach(function (c) {
          var r = c.getBoundingClientRect();
          var d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
          var t = clamp(1 - d / 260, 0, 1);
          c.style.setProperty('--w', Math.round(400 + t * 500));
          c.classList.toggle('is-hot', t > 0.55);
        });
      });
      zone.addEventListener('pointerleave', function () {
        chars.forEach(function (c) { c.style.setProperty('--w', 400); c.classList.remove('is-hot'); });
      });
    });
  }

  /* ---------- Theme switch with a circular reveal ---------- */
  function initModeToggle() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-s-mode-toggle]');
      if (!btn) return;
      var next = html.getAttribute('data-mode') === 'dark' ? 'light' : 'dark';
      function apply() {
        html.setAttribute('data-mode', next);
        try { localStorage.setItem('sultana-mode', next); } catch (err) {}
        $$('[data-s-mode-toggle]').forEach(function (b) { b.setAttribute('aria-pressed', next === 'dark' ? 'true' : 'false'); });
      }
      if (!document.startViewTransition || reduce) { apply(); return; }
      var r = btn.getBoundingClientRect();
      html.style.setProperty('--vx', r.left + r.width / 2 + 'px');
      html.style.setProperty('--vy', r.top + r.height / 2 + 'px');
      html.classList.add('s-vt-mode');
      var vt = document.startViewTransition(apply);
      vt.finished.finally(function () { html.classList.remove('s-vt-mode'); });
    });
  }

  /* ---------- Product image morphs into the product page ---------- */
  function initCardTransitions() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.s-card a[href*="/products/"], .s-card-wrap a[href*="/products/"]');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
      var card = link.closest('.s-card');
      var img = card && card.querySelector('.s-card__media img');
      $$('[style*="view-transition-name"]').forEach(function (el) { el.style.viewTransitionName = ''; });
      if (img) img.style.viewTransitionName = 's-hero-img';
    });
    window.addEventListener('pageshow', function () {
      $$('.s-card__media img').forEach(function (el) { el.style.viewTransitionName = ''; });
    });
  }

  /* ---------- Cart count bump ---------- */
  function initCartBump() {
    if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(function () {
        $$('.s-cart').forEach(function (c) {
          c.classList.remove('is-bump');
          void c.offsetWidth;
          c.classList.add('is-bump');
        });
      }, 300);
    });
  }

  function initAll(root) {
    initReveals(root);
    initMagnetic(root);
    initGlow(root);
    initParallax(root);
    initStacks(root);
    initRails(root);
    initWeight(root);
    requestScroll();
  }

  initHeader();
  initModeToggle();
  initCardTransitions();
  initCartBump();
  initAll(document);

  /* Markup swapped in later (related products, cart drawer, filters) */
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches('[data-s-reveal], [data-s-split], [data-s-magnetic]') ||
              node.querySelector('[data-s-reveal], [data-s-split], [data-s-magnetic]')) {
            initAll(node.parentNode || node);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (e) {
    function alive(el) { return document.documentElement.contains(el); }
    stacks = stacks.filter(alive);
    parallaxEls = parallaxEls.filter(alive);
    initAll(e.target);
  });
})();
